import { useEffect, useState } from 'react';
import { api, getSession } from '../api/client.ts';
import { verifierMiseAJour } from '../registerSW.ts';
import { memoriserPeriode, periodeConnue, periodeVoisine } from '../offline/periodesCache.ts';
import { rafraichir } from '../offline/dbModules.ts';
import { useModules } from '../hooks/useModules.ts';
import type { ResultatMiseAJour } from '../registerSW.ts';
import Icone from '../components/Icones.tsx';

export interface Periode {
  id: string;
  csb_id: string;
  annee: number;
  mois: number;
  statut: 'EN_COURS' | 'A_VERIFIER' | 'VALIDE' | 'CLOTURE';
}

const MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

export default function Accueil({ periode, onPeriodeChange, onAller }: { periode: Periode | null; onPeriodeChange: (p: Periode) => void; onAller?: (page: string) => void }) {
  const session = getSession();
  const now = new Date();
  const [annee, setAnnee] = useState(periode?.annee ?? now.getFullYear());
  const [mois, setMois] = useState(periode?.mois ?? now.getMonth() + 1);
  const [chargement, setChargement] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [verificationMaj, setVerificationMaj] = useState(false);
  const [resultatMaj, setResultatMaj] = useState<ResultatMiseAJour | null>(null);
  const m = useModules(periode);

  useEffect(() => { if (!periode && session.csbId) void selectionner(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function mettreAJour() {
    setVerificationMaj(true); setResultatMaj(null);
    try {
      const resultat = await verifierMiseAJour();
      setResultatMaj(resultat);
      if (resultat !== 'MISE_A_JOUR_APPLIQUEE') setVerificationMaj(false);
    } catch { setResultatMaj('ERREUR'); setVerificationMaj(false); }
  }

  /** Ouvre un mois. Sans réseau : possible pour un mois déjà ouvert sur cet appareil. Avec réseau : télécharge aussi le mois
   *  précédent et prépare le suivant, pour que la saisie reste possible plus tard sans connexion. */
  async function selectionner() {
    if (!session.csbId) return;
    setChargement(true); setMessage(null);
    try {
      const p = await api.post<Periode>('/periodes/ensure', { csbId: session.csbId, annee, mois });
      memoriserPeriode(p); onPeriodeChange(p);
      void (async () => {
        await rafraichir(p.id);
        for (const delta of [-1, 1] as const) {
          let mm = p.mois + delta, aa = p.annee; if (mm < 1) { mm = 12; aa--; } else if (mm > 12) { mm = 1; aa++; }
          try { const v = periodeConnue(aa, mm) ?? await api.post<Periode>('/periodes/ensure', { csbId: session.csbId, annee: aa, mois: mm }); memoriserPeriode(v); await rafraichir(v.id); } catch { /* préparation facultative */ }
        }
      })();
    } catch {
      const connue = periodeConnue(annee, mois);
      if (connue) { onPeriodeChange(connue); setMessage('Hors connexion : mois ouvert depuis cet appareil.'); }
      else setMessage('Impossible d\'ouvrir ce mois sans connexion : il n\'a pas encore été ouvert sur cet appareil.');
    } finally { setChargement(false); }
  }

  const prog: Record<string, boolean> = (m.calc?.progression as any) ?? {};
  const etapes: Array<[string, string, boolean | undefined]> = [
    ['Consultations (RCE)', 'RCE', undefined], ['PEV', 'PEV', prog.PEV], ['Intrants', 'STOCK', prog.STOCK], ['Finance', 'FINANCE', prog.FINANCE],
    ['Décès', 'CAUSES_DECES', prog.CAUSES_DECES], ['Communication', 'COMMUNICATION', prog.COMMUNICATION], ['Inventaire', 'INVENTAIRE', prog.INVENTAIRE],
  ];
  const faits = etapes.filter(([, , ok]) => ok).length;
  const precedente = periode ? periodeVoisine(periode, -1) : null;
  void precedente;

  return (
    <div className="accueil">
      <div className="accueil-entete">
        <h1>Accueil</h1>
        <button
          type="button"
          className="icone-maj"
          onClick={mettreAJour}
          disabled={verificationMaj}
          aria-label="Mettre à jour l'application"
          title="Mettre à jour l'application (vide le cache et recharge)"
        >
          <span className={verificationMaj ? 'tourne' : ''} aria-hidden="true"><Icone nom="sync" /></span>
        </button>
      </div>
      {resultatMaj === 'HORS_LIGNE' && <p className="message info">Mise à jour impossible hors connexion : reconnectez-vous puis réessayez.</p>}
      {resultatMaj === 'ERREUR' && <p className="messages-erreur">Échec de la mise à jour. Vérifiez votre connexion et réessayez.</p>}
      <section className="carte periode-choix">
        <div className="ligne-actions">
          <label>Mois<select value={mois} onChange={(e) => setMois(Number(e.target.value))}>{MOIS.map((mm, i) => <option key={mm} value={i + 1}>{mm}</option>)}</select></label>
          <label>Année<input type="number" min={2020} max={2100} value={annee} onChange={(e) => setAnnee(Number(e.target.value))} /></label>
          <button onClick={selectionner} disabled={chargement}>{chargement ? 'Ouverture…' : periode ? 'Changer de mois' : 'Ouvrir ce mois'}</button>
        </div>
        {periode && <p className="periode-active">Mois de travail : <b>{MOIS[periode.mois - 1]} {periode.annee}</b> · {periode.statut.replace('_', ' ').toLowerCase()}</p>}
        {message && <p className="message info">{message}</p>}
      </section>

      <div className="grille-cartes accueil-cartes">
        <button className="carte-action c1 principale" disabled={!periode} onClick={() => onAller?.('SAISIE')}><span className="icone" aria-hidden="true"><Icone nom="saisie" /></span><b>Saisie</b><span>Les saisies du mois</span></button>
        <button className="carte-action c2" disabled={!periode} onClick={() => onAller?.('PREINV')}><span className="icone" aria-hidden="true"><Icone nom="inv" /></span><b>Pré-inventaire / Inventaire</b><span>Comptage et FANOME</span></button>
        <button className="carte-action c3" onClick={() => onAller?.('RAPPORTS')}><span className="icone" aria-hidden="true"><Icone nom="rap" /></span><b>Rapports</b><span>RSH et DHIS2, du lundi au dimanche</span></button>
        <button className="carte-action c4" disabled={!periode} onClick={() => onAller?.('TABLEAUX')}><span className="icone" aria-hidden="true"><Icone nom="rma" /></span><b>RMA</b><span>Tableaux et exports</span></button>
        <button className="carte-action c5" onClick={() => onAller?.('COMMANDES')}><span className="icone" aria-hidden="true"><Icone nom="cmd" /></span><b>Bon de commande</b><span>Calculé sur 3 mois</span></button>
      </div>

      {periode && (
        <section className="carte avancement">
          <h2>Avancement du mois <span className="pastille info">{faits}/{etapes.length - 1}</span></h2>
          <ul className="etapes">{etapes.filter(([, id]) => id !== 'RCE').map(([label, , ok]) => <li key={label} className={ok ? 'fait' : ''}>{ok ? '✓' : '○'} {label}</li>)}</ul>
        </section>
      )}

      <details className="a-propos">
        <summary>À propos de l'outil</summary>
        <p><b>Outil pratique d'aide au comptage, à la consolidation et au suivi des données.</b> Il facilite le comptage et la préparation des données du RMA à partir du RCE, du registre CRENAS, du registre CPN,  de la fiche de pointage de vaccination et les fiches de stock des intrants, etc, limitant les doubles saisies et les erreurs de comptage.</p>
        <p>Il ne remplace ni les registres sources, ni les outils officiels, ni les directives en vigueur. Les cas du registre CRENAS sont pris en compte partiellement : les admissions déjà comptées dans le RCE ne doivent pas être comptées deux fois.</p>
        <p className="note">Vos saisies déjà enregistrées, synchronisées ou en attente, ne sont jamais affectées par une mise à jour.</p>
      </details>
    </div>
  );
}
